<script setup>
import { ref, computed, inject, toRef, onBeforeUnmount } from "vue";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/shared/ui/hover-card";
import { useUserCard } from "@/entities/user/composables/useUserCard";
import { USER_PROFILE_DRAWER_KEY } from "@/widgets/user-profile-drawer";
import UserCardContent from "./parts/UserCardContent.vue";
import UserCardSkeleton from "./parts/UserCardSkeleton.vue";
import UserCardError from "./parts/UserCardError.vue";

/**
 * Единый smart-компонент для всех контекстов (таблицы / чаты / kanban / drawers).
 * Hover/focus открывает карту; reka-ui сам управляет open state и safe polygon.
 *
 * Кеш и AbortController — в Pinia store userCardCache; несколько UserCard
 * на один и тот же userId делят данные.
 */
const props = defineProps({
  userId: { type: String, required: true },
  side: { type: String, default: "bottom" },
  align: { type: String, default: "start" },
  sideOffset: { type: Number, default: 8 },
  openDelay: { type: Number, default: 120 },
  closeDelay: { type: Number, default: 200 },
  editable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["edit", "open-profile"]);

const userProfileDrawer = inject(USER_PROFILE_DRAWER_KEY, null);

const { data, isLoading, isError, load, cancel } = useUserCard(
  toRef(props, "userId")
);

function onTriggerClick(e) {
  e?.stopPropagation?.();
  if (props.disabled) return;
  if (userProfileDrawer) {
    userProfileDrawer.open(props.userId);
  } else {
    emit("open-profile", props.userId);
  }
  dismiss();
}

// Принудительное закрытие после action (Open profile / Edit).
// После короткого тика возвращаем undefined → reka-ui снова сам управляет
// hover state и сможет открыть карту на повторный hover.
const forceClose = ref(false);
const openProp = computed(() => (forceClose.value ? false : undefined));
let resetTimer = null;

function dismiss() {
  forceClose.value = true;
  if (resetTimer) clearTimeout(resetTimer);
  resetTimer = setTimeout(() => {
    forceClose.value = false;
    resetTimer = null;
  }, 250);
}

function onOpenChange(value) {
  if (value) {
    if (props.disabled) return;
    load();
  }
}

onBeforeUnmount(() => {
  cancel();
  if (resetTimer) clearTimeout(resetTimer);
});
</script>

<template>
  <HoverCard
    :open="openProp"
    :open-delay="openDelay"
    :close-delay="closeDelay"
    @update:open="onOpenChange"
  >
    <HoverCardTrigger as-child>
      <span
        :data-user-id="userId"
        class="inline-flex outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[var(--radius)]"
        :aria-disabled="disabled || undefined"
        @click="onTriggerClick"
      >
        <slot />
      </span>
    </HoverCardTrigger>
    <HoverCardContent
      :side="side"
      :align="align"
      :side-offset="sideOffset"
      class="p-0"
    >
      <UserCardSkeleton v-if="isLoading && !data" />
      <UserCardError v-else-if="isError && !data" @retry="load" />
      <UserCardContent
        v-else-if="data"
        :user="data"
        :editable="editable"
        @edit="(id) => emit('edit', id)"
        @dismiss="dismiss"
      />
    </HoverCardContent>
  </HoverCard>
</template>
